import React, { useEffect, useState, useContext } from 'react';
import { Box, Tab, Typography } from '@mui/material';
import { PowerBIEmbed } from 'powerbi-client-react';
import {
  TokenType,
  BackgroundType,
  LayoutType,
  DisplayOption,
} from 'powerbi-models';
import './PowerBI.css';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import powerbiApi from './PowerBI.api';
import SpinningSpark from '../shared/components/SpinningSpark';
import colors from '../colors.styles';
import { UserContext } from '../auth/User.context';
import UsersApi from '../users/Users.api';

export default function PowerBI() {
  const { state } = useContext(UserContext);
  const userPerms = state?.userPermissions || [];
  const isAdmin = state?.user?.role_id === 1;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [visibleReports, setVisibleReports] = useState([]);
  const [selectedTab, setSelectedTab] = useState('');
  const [availableReports, setAvailableReports] = useState([]);

  const styles = {
    tabList: {
      '.MuiTabs-flexContainer': {
        borderBottom: `2px solid ${colors.base.light_gray}`,
      },
      '.MuiTabs-indicator': { backgroundColor: colors.base.spark_yellow },
      '.MuiButtonBase-root': {
        textTransform: 'capitalize',
        fontSize: '1rem',
        backgroundColor: colors.base.almost_white_blue,
        borderRadius: '10px 10px 0 0',
      },
      '.Mui-selected': {
        color: `${colors.base.light} !important`,
        backgroundColor: colors.base.walmart_blue,
      },
    },
  };

  useEffect(() => {
    powerbiApi
      .listPowerBIReports()
      .then((res) => {
        setAvailableReports(res.data || []);
      })
      .catch((err) => {
        console.error('Error loading PowerBI reports', err);
        setAvailableReports([]); // fail safe
      });
  }, []);

  // decide which tabs to show
  useEffect(() => {
    if (!availableReports.length) {
      setVisibleReports([]);
      setSelectedTab('');
      return;
    }

    const has = (title) => userPerms.some((p) => p.title === title);

    const enriched = availableReports.map((r) => ({
      ...r,
      perm: `View PowerBI: ${r.id}`,
    }));

    if (isAdmin) {
      setVisibleReports(enriched);
      setSelectedTab(enriched[0]?.id || '');
      return;
    }

    const hasGlobalPowerBI = has('View Video Report');
    const hasAnyGranularPowerBI = userPerms.some((p) =>
      p.title.startsWith('View PowerBI: ')
    );

    // If user doesn't even have the global "View Video Report" permission,
    // they shouldn't see any reports at all.
    if (!hasGlobalPowerBI) {
      setVisibleReports([]);
      setSelectedTab('');
      return;
    }

    // If user has no granular perms at all → treat global as "see all reports".
    if (!hasAnyGranularPowerBI) {
      setVisibleReports(enriched);
      setSelectedTab(enriched[0]?.id || '');
      return;
    }

    // User has granular perms; show only the ones they explicitly have.
    const granular = enriched.filter((r) => has(r.perm));

    // if they have granular perms but none match any configured report,
    // they should see nothing
    if (granular.length === 0) {
      setVisibleReports([]);
      setSelectedTab('');
      return;
    }

    setVisibleReports(granular);
    setSelectedTab(granular[0]?.id || '');
  }, [isAdmin, userPerms, availableReports]);

  useEffect(() => {
    if (!selectedTab) return;

    const fetchCredentials = async () => {
      setLoading(true);
      setError(false);

      try {
        let region = null;

        // Only fetch region if we're loading the Activity report
        if (selectedTab === 'activity' && state?.user?.mailId) {
          // Step 1: lookup user by email to get platform_id
          const searchRes = await UsersApi.getUsers(state.user.mailId);
          const foundUser = searchRes?.data?.data?.[0];
          const platformId = foundUser?.platform_id;

          // Step 2: fetch detailed user info by platform_id to get region
          if (platformId) {
            const userRes = await UsersApi.getUser(platformId);
            region = userRes?.data?.activity_dashboard_access;
          }
        }

        // Step 3: call PowerBI credentials with region (if any)
        const resp = await powerbiApi.getPowerBICredentials(
          selectedTab,
          region
        );
        setCredentials(resp.data);
      } catch (err) {
        console.error('Error fetching PowerBI credentials:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCredentials();
  }, [selectedTab]);

  const embedConfig = {
    type: 'report',
    tokenType: TokenType.Embed,
    settings: {
      panes: { filters: { expanded: false, visible: false } },
      background: BackgroundType.Transparent,
      layoutType: LayoutType.Custom,
      customLayout: { displayOption: DisplayOption.FitToWidth },
      navContentPaneEnabled: false,
      visualSettings: {
        visualHeaders: [
          {
            settings: { visible: false },
            selector: {
              $schema: 'http://powerbi.com/product/schema#visualSelector',
              visualName: '7379e14d7e10f32927e6',
            },
          },
        ],
      },
    },
  };

  const reportBox = (tabId) => {
    if (tabId !== selectedTab) return null;

    if (loading) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center">
          <SpinningSpark />
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="subtitle1">
            Failed to load PowerBI report.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <PowerBIEmbed
          embedConfig={{
            ...embedConfig,
            accessToken: credentials?.embedToken,
            embedUrl: credentials?.url,
          }}
          cssClassName="report-container"
        />
      </Box>
    );
  };

  if (visibleReports.length === 0) {
    return (
      <Typography variant="subtitle1">
        You don&apos;t have access to any Power BI reports.
      </Typography>
    );
  }

  return (
    <TabContext value={selectedTab}>
      <TabList onChange={(_, v) => setSelectedTab(v)} sx={styles.tabList}>
        {visibleReports.map((r) => (
          <Tab key={r.id} label={r.label} value={r.id} />
        ))}
      </TabList>
      {visibleReports.map((r) => (
        <TabPanel key={r.id} value={r.id}>
          {reportBox(r.id)}
        </TabPanel>
      ))}
    </TabContext>
  );
}
